<script setup lang="ts">
import { FilePlus, Loader2, MessageCirclePlus, Plus } from 'lucide-vue-next';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@/components/ui/tooltip';
import { useChatSessionsStore } from '@/stores/chatSessions';
import { useConfigCatStore } from '@/stores/configCat';

const chatSessionsStore = useChatSessionsStore();
const configCatStore = useConfigCatStore();
const router = useRouter();

const props = defineProps<{
    isSidebarOpen: boolean;
    isCreatingPresentation?: boolean;
}>();

const emits = defineEmits<{
    (e: 'createChat'): void;
    (e: 'createPresentation'): void;
}>();

const isInWelcomeChatView = computed(
    () => router.currentRoute.value.path === '/chat'
);

const createChat = () => {
    if (!isInWelcomeChatView.value) {
        chatSessionsStore.isRedirectingToNewChat = true;
    }
    emits('createChat');
};

const createPresentation = () => {
    emits('createPresentation');
};
</script>

<template>
    <div
        class="flex items-center"
        :class="
            props.isSidebarOpen ? 'flex-row gap-2' : 'flex-col gap-5 w-full'
        "
    >
        <Tooltip :disabled="props.isSidebarOpen">
            <TooltipTrigger as-child>
                <Button
                    size="sm"
                    @click="createChat"
                    class="flex-1 w-full gap-1 items-center rounded-full transition-background duration-0"
                    :class="
                        props.isSidebarOpen
                            ? 'pl-2 pr-3'
                            : 'p-0 w-full bg-transparent text-foreground shadow-none hover:bg-transparent'
                    "
                    :disabled="chatSessionsStore.isRedirectingToNewChat"
                >
                    <component
                        :is="
                            chatSessionsStore.isRedirectingToNewChat
                                ? Loader2
                                : props.isSidebarOpen
                                  ? Plus
                                  : MessageCirclePlus
                        "
                        :class="[
                            chatSessionsStore.isRedirectingToNewChat
                                ? 'animate-spin'
                                : '',
                            props.isSidebarOpen
                                ? 'w-4 h-4'
                                : 'w-[1.35rem] h-[1.35rem]'
                        ]"
                    />
                    <p class="whitespace-nowrap" v-if="props.isSidebarOpen">
                        Chat
                    </p>
                </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
                <p>New Chat</p>
            </TooltipContent>
        </Tooltip>

        <Tooltip
            v-if="configCatStore.isPowerpointEnabled"
            :disabled="props.isSidebarOpen"
        >
            <TooltipTrigger as-child>
                <Button
                    size="sm"
                    @click="createPresentation"
                    class="flex-1 w-full gap-1 items-center rounded-full transition-background duration-0"
                    :class="
                        props.isSidebarOpen
                            ? 'pl-2 pr-3'
                            : 'p-0 w-full bg-transparent text-foreground shadow-none hover:bg-transparent'
                    "
                    :disabled="props.isCreatingPresentation"
                >
                    <component
                        :is="
                            props.isCreatingPresentation
                                ? Loader2
                                : props.isSidebarOpen
                                  ? Plus
                                  : FilePlus
                        "
                        :class="[
                            props.isCreatingPresentation ? 'animate-spin' : '',
                            props.isSidebarOpen
                                ? 'w-4 h-4'
                                : 'w-[1.35rem] h-[1.35rem]'
                        ]"
                    />
                    <p class="whitespace-nowrap" v-if="props.isSidebarOpen">
                        Slides
                    </p>
                </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
                <p>New Slides</p>
            </TooltipContent>
        </Tooltip>
    </div>
</template>
